import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import { inviteContactsViaSms } from '../../utils/contacts';

function InviteContactsButton({ message, className = '', variant = 'primary', disabled = false }) {
  const { t, formatMessage } = useLanguage();
  const ic = t.enrola.inviteContacts;
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!message?.trim() || loading || disabled) return;

    setLoading(true);
    try {
      const result = await inviteContactsViaSms(message);

      if (result.method === 'contact-picker-sms') {
        toast.success(formatMessage(ic.openingSms, { count: result.count }));
      } else if (result.method === 'sms-compose') {
        toast.success(ic.openingMessages);
      }
    } catch (error) {
      if (error?.message === 'CANCELLED') {
        return;
      }

      if (error?.message === 'DESKTOP_UNSUPPORTED') {
        try {
          await copyTextToClipboard(message);
          toast.success(ic.desktopCopied);
        } catch {
          toast.error(ic.failed);
        }
        return;
      }

      toast.error(ic.failed);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'compact') {
    return (
      <motion.button
        type="button"
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        onClick={handleClick}
        disabled={disabled || loading}
        title={ic.label}
        className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/25 ring-1 ring-white/25 transition-opacity disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        <DevicePhoneMobileIcon className="h-5 w-5" />
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      onClick={handleClick}
      disabled={disabled || loading}
      className={`inline-flex w-full items-center justify-center gap-2 rounded-xl border border-sky-200/80 bg-gradient-to-r from-sky-500/10 to-blue-500/10 px-4 py-2.5 text-sm font-semibold text-sky-900 transition-opacity disabled:cursor-not-allowed disabled:opacity-50   ${className}`}
    >
      <DevicePhoneMobileIcon className="h-5 w-5 shrink-0" />
      {loading ? ic.loading : ic.label}
    </motion.button>
  );
}

export default InviteContactsButton;
